import queryClient from "api/queryClient.ts";
import { useUpdateIntegrationMutation } from "api/TenantSlug/Integration/hooks";
import { ErrorsChartSection } from "components/Forms/ConnectionCreatorGeneralFormSections/ErrorsChartSection";
import { InformationSection } from "components/Forms/ConnectionCreatorGeneralFormSections/InformationSection";
import { IntegrationCustomFieldsSection } from "components/Forms/ConnectionCreatorGeneralFormSections/IntegrationGeneralFormSection/IntegrationCustomFieldsSection";
import { IntegrationGeneralFormSection } from "components/Forms/ConnectionCreatorGeneralFormSections/IntegrationGeneralFormSection/IntegrationGeneralFormSection";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect } from "react";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { formSchema, InformationFormData } from "./formSchema";

import IntegrationData = App.Data.IntegrationData;

const transformIntegrationData = (integration: IntegrationData): InformationFormData => {
    const integrationData = { ...integration };
    delete integrationData.formFields;

    return {
        ...integrationData,
        offers: integration.offers?.map((offer) => offer.id) || [],
        category: integration.category?.id ?? null,
        label_ids: integration.labels?.map((label) => label.id) || [],
        // Default to false until BE adds the field
        use_new_connection_creator:
            (integration as IntegrationData & { use_new_connection_creator?: boolean }).use_new_connection_creator ??
            false,
    };
};

interface ApiToolGeneralFormProps {
    integration: IntegrationData;
}

interface ApiToolGeneralFormContentProps {
    integration: IntegrationData;
}

const ApiToolGeneralFormContent = ({ integration }: ApiToolGeneralFormContentProps) => {
    const generalFormUtils = useGeneralForm<InformationFormData>();
    const { reset } = generalFormUtils;

    // Reset form when integration changes (after refetch)
    useEffect(() => {
        if (integration) {
            reset(transformIntegrationData(integration));
        }
    }, [integration, reset]);
    return (
        <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground>
                    <IntegrationGeneralFormSection
                        integration={integration}
                        exportPermission={PermissionEnum.ToolIntegrationExportView}
                    />
                </SectionContentWrapper>
                <SectionContentWrapper withBackground>
                    <IntegrationCustomFieldsSection integration={integration} />
                </SectionContentWrapper>
            </div>
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground>
                    <InformationSection />
                </SectionContentWrapper>
                <SectionContentWrapper withBackground>
                    <ErrorsChartSection />
                </SectionContentWrapper>
            </div>
        </div>
    );
};

export const ApiToolGeneralForm: FC<ApiToolGeneralFormProps> = ({ integration }) => {
    const updateIntegrationMutation = useUpdateIntegrationMutation(integration?.id || "");
    const { t: tF } = usePaldockTranslation("forms.api-tool-general");

    if (!integration) return null;

    const handleSubmit = (values: InformationFormData) => {
        const payload = {
            ...integration,
            ...values,
            country_id: values.country_id ?? null,
            category_id: values.category ?? null,
        };

        updateIntegrationMutation.mutate(
            { ...payload },
            {
                onSuccess: () => {
                    toast.success(tF("messages.update_success"));
                    queryClient.refetchQueries({ queryKey: ["getIntegration", integration.id] });
                    // onSubmit?.();
                },
                onError: () => {
                    toast.error(tF("messages.update_error"));
                },
            }
        );
    };

    const defaultValues = transformIntegrationData(integration);

    return (
        <GeneralFormProvider<InformationFormData>
            schema={formSchema}
            id={FormId.ApiToolGeneralForm}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <ApiToolGeneralFormContent integration={integration} />
        </GeneralFormProvider>
    );
};
